import React from "react";
import { createPortal } from "react-dom";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStore from "../../Store/Store";

const AutoDiagramGenerationOverlay: React.FC = () => {
  const theme = useTheme();
  const { autoRepoDiagramProgress, autoRepoDiagramConfig } = useStore(
    (state) => ({
      autoRepoDiagramProgress: state.autoRepoDiagramProgress,
      autoRepoDiagramConfig: state.autoRepoDiagramConfig,
    })
  );

  // Don't show if not generating
  if (!autoRepoDiagramProgress.isGenerating) {
    return null;
  }

  // Calculate progress percentage
  const progressPercent =
    autoRepoDiagramProgress.total > 0
      ? Math.min(
          (autoRepoDiagramProgress.current / autoRepoDiagramProgress.total) *
            100,
          100
        )
      : 0;

  const overlay = (
    <Box
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100vh"
      bgcolor="rgba(255, 255, 255, 0.05)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={10000050}
      style={{ backdropFilter: "blur(1px)" }}
    >
      <Box
        bgcolor="background.paper"
        p={4}
        borderRadius="20px"
        boxShadow={theme.shadows[8]}
        minWidth={400}
        maxWidth={500}
        textAlign="center"
      >
        <Typography variant="h6" mb={2} color="text.primary">
          Generating Repository Diagram
        </Typography>

        <Typography variant="body2" mb={3} color="text.secondary">
          Processing{" "}
          <strong>
            {autoRepoDiagramProgress.currentFolderName ||
              "repository structure"}
          </strong>
        </Typography>

        <Box mb={2}>
          <Typography variant="body2" mb={1} color="text.secondary">
            Level {autoRepoDiagramProgress.currentLevel} of{" "}
            {autoRepoDiagramProgress.totalLevels}
            {" • "}
            {autoRepoDiagramProgress.current} of {autoRepoDiagramProgress.total}{" "}
            items ({Math.round(progressPercent)}%)
          </Typography>

          <Typography variant="body2" mb={1} color="text.secondary">
            Cells created: {autoRepoDiagramProgress.currentCells || 0} /{" "}
            {autoRepoDiagramConfig.maxCells}
          </Typography>

          <Box
            position="relative"
            height={12}
            borderRadius={2}
            overflow="hidden"
            bgcolor={theme.custom?.lightGrey || "#f0f0f0"}
          >
            <Box
              position="absolute"
              left={0}
              top={0}
              bottom={0}
              width={`${progressPercent}%`}
              bgcolor={theme.custom?.pink || "#e91e63"}
              sx={{
                transition: "width 0.5s ease, box-shadow 0.5s ease",
                boxShadow: `0 0 8px ${theme.custom?.pink || "#e91e63"}`,
              }}
            />
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary">
          Creating cells and linking files to diagram nodes...
        </Typography>
      </Box>
    </Box>
  );

  return createPortal(overlay, document.body);
};

export default AutoDiagramGenerationOverlay;
